<template>
  <div
    v-if="!loading"
    :class="$style.base"
  >
    <BaseGrid
      container
      noPadding
    >
      <BaseGrid
        :class="$style.index"
        :size="4"
        noPadding
        v-intercom="
          settings.emailType
            ? `marketing-builder-settings-${settings.emailType.toLowerCase()}`
            : null
        "
      >
        <Settings>
          <slot name="actions" />
        </Settings>
      </BaseGrid>
      <BaseGrid
        :size="8"
        noPadding
      >
        <FiltersBar
          v-show="showFilters"
          @setFilter="setFilter"
          @close="closeFilters"
          :fullHeight="true"
        />
        <Preview />
      </BaseGrid>
    </BaseGrid>
  </div>
</template>

<script lang="ts">
import { useQuery } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import { storeToRefs } from 'pinia';
import { defineComponent, ref } from 'vue';
import { useRoute } from 'vue-router';
import FiltersBar from '@/components/customer-filters/FiltersBar.vue';
import { emailTemplateFragment } from '@/graphql-fragments';
import { useCompanyStore } from '@/stores/company';
import { useServicesStore } from '@/stores/services';
import { useMarketingStore } from '../../store';
import { useMarketingBuilderStore } from '../../store/builder';
import Preview from './preview/index.vue';
import Settings from './Settings.vue';

export default defineComponent({
  components: {
    Settings,
    Preview,
    FiltersBar,
  },
  props: {
    settings: {
      type: Object,
      required: true,
      validator: (value) =>
        !!value.title &&
        !!value.description &&
        !!value.sentDescription &&
        !!value.receiveDescription,
    },
    modelValue: {
      type: Boolean,
    },
  },
  emits: ['update:modelValue'],
  setup(props) {
    const route = useRoute();
    const { setAttrs } = useMarketingStore();
    const loading = ref(false);
    const { company } = useCompanyStore();
    const builderStore = useMarketingBuilderStore();

    builderStore.setSettings(props.settings);
    if (props.settings.showToggle) {
      builderStore.setEnabled(props.modelValue);
    }

    if (route?.params?.id) {
      loading.value = true;

      const { result, onResult } = useQuery(
        gql`
          query getEmailTemplate($id: ID!, $emailType: EmailTemplateEnum!) {
            emailTemplate(id: $id, emailType: $emailType) {
              ...emailTemplate
            }
            emailTemplateDefault: emailTemplate(
              id: $id
              emailType: $emailType
              defaultValues: true
            ) {
              ...emailTemplate
            }
          }
          ${emailTemplateFragment}
        `,
        {
          id: route.params.id,
          emailType: 'MARKETING_CAMPAIGN',
        },
      );

      onResult(() => {
        if (result.value.emailTemplate) {
          result.value.emailTemplate.emailTemplateDefault =
            result.value.emailTemplateDefault;
          setAttrs(result.value.emailTemplate);
        }

        loading.value = false;
      });

      onResult(() => {
        loading.value = false;
      });
    }

    return {
      loading,
      company,
      builderStore,
    };
  },
  watch: {
    enabled: {
      handler(newValue, oldValue) {
        if (oldValue !== null) {
          this.$emit('update:modelValue', newValue);
        }
      },
    },
  },
  computed: {
    showFilters() {
      const { showFilters } = useMarketingStore();
      return showFilters;
    },
    enabled() {
      return this.builderStore.enabled;
    },
  },
  methods: {
    enableTemplate() {
      this.loading = true;

      const { rebookEnabledServicesCount } = useServicesStore();

      if (rebookEnabledServicesCount === 0) {
        this.setAllServicesEnabled();
        this.$router.push({ name: 'default-rebook-reminder-services' });
      } else {
        this.builderStore.setEnabled(true);
        this.loading = false;
      }
    },
    setAllServicesEnabled() {
      const { categoriesWithServices } = useServicesStore();
      const bulkUpdateServiceAttributes = categoriesWithServices
        .map((category) =>
          category.services.map((service) => ({
            id: service.id,
            name: service.name,
            rebookEnabled: true,
            rebookPeriod: 30,
          })),
        )
        .reduce((acc, val) => acc.concat(val), []);

      this.$apollo
        .mutate({
          mutation: gql`
            mutation updateServices($input: UpdateServicesInput!) {
              updateServices(input: $input) {
                services {
                  id
                }
              }
            }
          `,
          variables: {
            input: {
              bulkUpdateServiceAttributes,
            },
          },
        })
        .then(() => {
          const { getServices } = useServicesStore();
          getServices();
          this.builderStore.setEnabled(true);
          this.loading = false;
        });
    },
    setFilter(filter) {
      const { setFilter } = useMarketingStore();
      setFilter(filter);
    },
    closeFilters() {
      const { showFilters } = storeToRefs(useMarketingStore());
      showFilters.value = false;
    },
  },
  beforeUnmount() {
    const { resetToEmptyState } = useMarketingStore();
    resetToEmptyState();
    this.builderStore.resetState();
  },
});
</script>

<style
  lang="scss"
  module
>
.base {
  height: 100%;
  margin: -$spacing;
}

.index {
  z-index: 2;
}

.emptyPageWrapper {
  padding: 0.5 * $spacing;
}
</style>
